import { getLogger } from '../../commons/logger';
import { featureFlagsService } from '../../commons/featureFlagsService';
import { getSessionPlayer } from '../../commons/getSessionPlayerRequire';
import { W3C_ELEMENT_ID } from '../constants';
import type { WebDriver } from '../webdriver';
import type { LegacyElement, W3CElement } from '../../utils';
import type { Step } from 'clickim/src/common/models/step';
import type { FrameTree } from 'clickim/src/background/frameTree';
import type { LocateStep } from 'clickim/src/common/models/locateStep';
import type { FrameManager } from 'clickim/src/background/frameManager';
import type { PlaybackContext } from 'clickim/src/background/playbackContext';
import type { FrameLocatorInstance } from 'clickim/src/background/frameLocator';
import type { FrameHandler as ClickimFrameHandler } from 'clickim/src/locators/dynamicParentUtils';
import type { WebLocateElementPlayer as WebLocateElementPlayerClass, WebLocateElementResult } from 'clickim/src/background/webLocateElementPlayer';

const logger = getLogger('frame-locator');
const SELENIUM_ELEMENT_KEY = 'ELEMENT';

const _getGuidFromSeleniumElement = (seleniumElement: W3CElement | LegacyElement) => {
    if (!seleniumElement) {
        return null;
    }

    return (seleniumElement as LegacyElement)[SELENIUM_ELEMENT_KEY] || (seleniumElement as W3CElement)[W3C_ELEMENT_ID];
};

interface FrameHandler extends ClickimFrameHandler {
    seleniumFrameElement?: W3CElement | LegacyElement;
}

export function frameLocatorFactory(driver: WebDriver) {
    class FrameLocator implements FrameLocatorInstance {
        private readonly _cache: Record<string, string> = {};
        private currentFrameHandler?: FrameHandler;

        constructor(
            public readonly frameManager: FrameManager,
            private readonly LocateElementPlayer: typeof WebLocateElementPlayerClass,
        ) {}

        private cacheResults(seleniumGuid: string, resultsUrl: string) {
            this._cache[seleniumGuid] = resultsUrl;
        }

        private getResultsFromCache(seleniumGuid: string) {
            return this._cache[seleniumGuid];
        }

        private cacheFrameLocateResults(frameHandler: FrameHandler | undefined) {
            if (frameHandler?.seleniumFrameElement && frameHandler.frameLocateResultUrl) {
                const guid = _getGuidFromSeleniumElement(frameHandler.seleniumFrameElement);
                if (guid) {
                    this.cacheResults(guid, frameHandler.frameLocateResultUrl);
                }
            }
        }

        private async foundFrameCallback(result: WebLocateElementResult, frameTree: FrameTree, testimFrameId: string | number): Promise<FrameHandler> {
            const { frameOffset, locatedElement } = result;
            const { locatorBuilderUtils } = getSessionPlayer();
            if (locatorBuilderUtils.isEmptyResult(locatedElement)) {
                const errorMsg = 'got empty result in frame result, not rejected from locate element player';
                logger.error(errorMsg);
                throw new Error(errorMsg);
            }
            const el = await driver.switchToLocatedFrame(locatedElement);
            if (!el.value) {
                throw new Error('Element not found');
            }
            const guid = _getGuidFromSeleniumElement(el.value);
            const frameLocateResultUrl = this.getResultsFromCache(guid!);

            return {
                url: '',
                frameId: -1,
                frameOffset,
                tabInfo: frameTree.tabInfo,
                tabId: frameTree.tabId,
                testimFrameId,
                testimFullFrameId: `${this.currentFrameHandler!.testimFullFrameId}-${testimFrameId}`,
                seleniumFrameElement: el.value,
                frameLocateResultUrl,
            };
        }

        private async locate(frameLocator: LocateStep, frameDepth: number, currentFrame: FrameHandler, context: PlaybackContext, frameTree: FrameTree, stepData: Step) {
            const locateElementPlayer = new this.LocateElementPlayer(context);
            frameLocator.targetId = `frameLocator_${frameDepth}`;
            const locateResult = await locateElementPlayer.locate(frameLocator, currentFrame, frameLocator.targetId);
            locateResult.isVisible = true; // frame visibility check is done on the target element
            const handledLocateResult = await locateElementPlayer.handleLocateResult(locateResult, stepData, frameLocator).catch(() => { throw new Error(''); }); // silence [object object] errors;
            const { locatedElement } = context.data[frameLocator.targetId];
            const location = await driver.getElementLocationWithPadding(locatedElement);
            const value = location.value || { top: 0, left: 0 };
            handledLocateResult.frameOffset = {
                top: currentFrame.frameOffset!.top + value.top,
                left: currentFrame.frameOffset!.left + value.left,
            };
            if (locateElementPlayer.addFrameDataToContext) {
                locateElementPlayer.addFrameDataToContext(handledLocateResult.targetId, handledLocateResult.locateResult);
            }
            const frameHandler = await this.foundFrameCallback(handledLocateResult as WebLocateElementResult, frameTree, frameLocator.testimFrameId!);
            this.currentFrameHandler = frameHandler;
            return frameHandler;
        }

        public async findFrame(stepData: Step, frameLocators: LocateStep[], context: PlaybackContext, frameTree: FrameTree) {
            const allowNoFrameSwitch = featureFlagsService.flags.enableFrameSwitchOptimization.isEnabled();
            const chronologicalResults = context.playback.resultsHandler.resultsByChronologicOrder;
            const lastResult = chronologicalResults.at(-1);
            const allowedRetries = 1;
            const moreThanAllowedRetries = lastResult && lastResult.stepId === stepData.id && lastResult.results.length > allowedRetries;
            if (allowNoFrameSwitch && !moreThanAllowedRetries && this.currentFrameHandler) {
                const currentFramePos = frameLocators.findIndex(x => x.testimFrameId === this.currentFrameHandler!.testimFrameId);
                if (currentFramePos > -1) {
                    const shorterPath = frameLocators.slice(currentFramePos + 1);
                    let frameIndex = 0;
                    // Changes to the deepest frame
                    for (const frameLocator of shorterPath) {
                        frameIndex++;
                        this.currentFrameHandler = await this.locate(frameLocator, frameIndex, this.currentFrameHandler as any, context, frameTree, stepData);
                    }
                    return this.currentFrameHandler;
                }
            }

            const topFrameHandler = await frameTree.getTopFrameHandler();
            topFrameHandler.frameOffset = { top: 0, left: 0 };
            const switchToTop = (allowNoFrameSwitch && this.currentFrameHandler === topFrameHandler) ?
                this.currentFrameHandler :
                driver.switchToTopFrame();
            await switchToTop;
            this.cacheFrameLocateResults(this.currentFrameHandler);
            this.currentFrameHandler = topFrameHandler;
            let frameIndex = 0;
            let currentFrame = topFrameHandler;
            for (const frameLocator of frameLocators) {
                frameIndex++;
                currentFrame = await this.locate(frameLocator, frameIndex, currentFrame as any, context, frameTree, stepData);
            }
            return currentFrame;
        }
    }

    return FrameLocator;
}
